import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ZP } from "../compile";
import { newElement } from "../schema";

const PX = ZP.MM_TO_PX; // css px per mm
const GUTTER = 30; // mm of grey space left of the paper (band labels live here)
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

const r2 = (n) => Math.round(n * 100) / 100;

// Styles that only exist in the editor (selection, handles, labels).
const EDITOR_CSS = `
.ib-band{outline:1px dashed rgba(39,125,254,.35);outline-offset:-1px}
.ib-band.ib-sel{outline:1.5px solid #277DFE}
.ib-band-label{position:absolute;top:0;font:600 9px/1.2 Arial,sans-serif;color:#475467;background:#fff;border:1px solid #d0d5dd;border-radius:3px;padding:2px 4px;cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;transform-origin:top right}
.ib-band-label.ib-sel{background:#277DFE;color:#fff;border-color:#277DFE}
.ib-band-resize{position:absolute;left:0;right:0;bottom:-3px;height:6px;cursor:ns-resize;z-index:30}
.ib-band-resize:hover,.ib-band-resize.ib-on{background:rgba(39,125,254,.35)}
.ib-el{position:absolute;cursor:move}
.ib-el>.ib-content{position:absolute;inset:0;pointer-events:none}
.ib-el:hover{outline:1px solid rgba(39,125,254,.6)}
.ib-sel-frame{position:absolute;outline:1.5px solid #277DFE;cursor:move;z-index:35}
.ib-el.ib-hidden>.ib-content{opacity:.35}
.ib-el.ib-err{outline:1.5px dashed #F51D00}
.ib-h{position:absolute;width:7px;height:7px;background:#fff;border:1.5px solid #277DFE;border-radius:1px;z-index:25}
.ib-guide{position:absolute;background:#ff2d95;pointer-events:none;z-index:40}
.ib-pagebreak{position:absolute;left:0;right:0;border-top:1px dashed #F51D00;pointer-events:none;z-index:15}
.ib-pagebreak span{position:absolute;right:0;top:-12px;font:9px Arial;color:#F51D00;background:#fff;padding:0 3px}
.ib-col-h{position:absolute;top:0;width:6px;margin-left:-3px;cursor:col-resize;z-index:26}
.ib-col-h:hover,.ib-col-h.ib-on{background:rgba(39,125,254,.45)}
.ib-tbl{position:relative;cursor:pointer}
.ib-tbl.ib-sel{outline:1.5px solid #277DFE}
.ib-tbl th[data-zp-col].ib-colsel{box-shadow:inset 0 0 0 2px #277DFE}
.ib-tbl-edge{position:absolute;top:0;bottom:0;width:6px;cursor:ew-resize;z-index:26}
.ib-tbl-edge:hover{background:rgba(39,125,254,.35)}
.ib-badge{position:absolute;top:-14px;left:0;font:9px Arial;background:#F51D00;color:#fff;padding:1px 4px;border-radius:2px;white-space:nowrap;z-index:30}
.ib-drop{outline:2px solid #12B76A!important}
`;

function handleStyle(h) {
  const s = {};
  if (h.includes("n")) s.top = -4;
  if (h.includes("s")) s.bottom = -4;
  if (h.includes("w")) s.left = -4;
  if (h.includes("e")) s.right = -4;
  if (h === "n" || h === "s") s.left = "calc(50% - 4px)";
  if (h === "e" || h === "w") s.top = "calc(50% - 4px)";
  s.cursor = { n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize", ne: "nesw-resize", sw: "nesw-resize", nw: "nwse-resize", se: "nwse-resize" }[h];
  return s;
}

// "Show formulas" mode: placeholders are shown instead of sample values.
function designVersion(el) {
  if (el.type === "text") return { ...el, content: String(el.content || "").replace(/\{\{\s*([\s\S]*?)\s*\}\}/g, "[$1]") };
  if (el.type === "field") return { ...el, type: "text", content: `[${el.expr || "empty"}]${el.format ? " · " + el.format : ""}` };
  return el;
}

function snapPositions(moving, others, bandW, bandH, threshold) {
  // returns { dx, dy, guides } to snap the moving box to nearby edges
  const xs = [0, bandW / 2, bandW];
  const ys = [0, bandH / 2, bandH];
  others.forEach((o) => {
    xs.push(o.x, o.x + o.w / 2, o.x + o.w);
    ys.push(o.y, o.y + o.h / 2, o.y + o.h);
  });
  const mx = [moving.x, moving.x + moving.w / 2, moving.x + moving.w];
  const my = [moving.y, moving.y + moving.h / 2, moving.y + moving.h];
  let best = { dx: 0, gx: null, ex: threshold + 1, dy: 0, gy: null, ey: threshold + 1 };
  mx.forEach((m) =>
    xs.forEach((x) => {
      const d = Math.abs(m - x);
      if (d < best.ex) best = { ...best, ex: d, dx: x - m, gx: x };
    })
  );
  my.forEach((m) =>
    ys.forEach((y) => {
      const d = Math.abs(m - y);
      if (d < best.ey) best = { ...best, ey: d, dy: y - m, gy: y };
    })
  );
  const guides = [];
  if (best.ex <= threshold) guides.push({ x: best.gx });
  else best.dx = 0;
  if (best.ey <= threshold) guides.push({ y: best.gy });
  else best.dy = 0;
  return { dx: best.dx, dy: best.dy, guides };
}

function ElementView({ el, ctx, selected, showFormulas, onMouseDown, onDoubleClick, editing, onEditDone }) {
  const res = useMemo(() => ctx.renderElement(showFormulas ? designVersion(el) : el, { noPosition: true, ignoreVisibility: true }), [ctx, el, showFormulas]);
  const hidden = useMemo(() => !ctx.isVisible(el, ctx.rootScope(), []), [ctx, el]);
  const err = res.errors.length > 0;
  const st = el.style || {};
  return (
    <div
      data-el-id={el.id}
      className={`ib-el ${selected ? "ib-sel" : ""} ${hidden ? "ib-hidden" : ""} ${err ? "ib-err" : ""}`}
      style={{ left: el.x + "mm", top: el.y + "mm", width: el.w + "mm", height: el.h + "mm" }}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      title={err ? res.errors.join("\n") : hidden ? "Hidden on this sample (visibility condition)" : ""}
    >
      <div className="ib-content" dangerouslySetInnerHTML={{ __html: res.html }} />
      {err && selected && <div className="ib-badge">{res.errors[0]}</div>}
      {editing && (
        <textarea
          autoFocus
          defaultValue={el.content}
          className="absolute inset-0 w-full h-full resize-none outline-none border border-primary bg-white p-0.5"
          style={{ fontSize: (st.fontSize || 9) + "pt", fontFamily: st.fontFamily || "inherit", fontWeight: st.bold ? 700 : 400, textAlign: st.align || "left", lineHeight: st.lineHeight || 1.2, minHeight: "6mm", zIndex: 50 }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Escape") onEditDone(null);
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) onEditDone(e.target.value);
          }}
          onBlur={(e) => onEditDone(e.target.value)}
        />
      )}
    </div>
  );
}

function TableView({ el, band, ctx, selection, setSelection, startColResize, startEdgeResize }) {
  const errors = [];
  const html = ctx.renderTable(el, errors);
  const isSel = selection.ids.includes(el.id);
  const ref = useRef(null);
  const [colEdges, setColEdges] = useState([]);
  const [headH, setHeadH] = useState(0);

  // measure column boundaries from the rendered header (runs after every
  // render on purpose; the setters bail out when nothing changed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const ths = Array.from(root.querySelectorAll("th[data-zp-col]"));
    const box = root.getBoundingClientRect();
    const scale = box.width / root.offsetWidth || 1;
    const edges = ths.slice(0, -1).map((th) => ({ id: th.getAttribute("data-zp-col"), left: Math.round(((th.getBoundingClientRect().right - box.left) / scale) * 10) / 10 }));
    setColEdges((prev) => (JSON.stringify(prev) === JSON.stringify(edges) ? prev : edges));
    const hh = ths[0] ? ths[0].offsetHeight : 0;
    setHeadH((prev) => (prev === hh ? prev : hh));
    ths.forEach((th) => th.classList.toggle("ib-colsel", isSel && th.getAttribute("data-zp-col") === selection.colId));
  });

  return (
    <div
      ref={ref}
      className={`ib-tbl ${isSel ? "ib-sel" : ""}`}
      style={{ marginLeft: (el.x || 0) + "mm", width: (el.w || 10) + "mm" }}
      onMouseDown={(e) => {
        e.stopPropagation();
        const th = e.target.closest && e.target.closest("th[data-zp-col]");
        setSelection({ bandId: band.id, ids: [el.id], colId: th ? th.getAttribute("data-zp-col") : null });
      }}
      title={errors.join("\n")}
    >
      <div dangerouslySetInnerHTML={{ __html: html }} />
      {isSel &&
        colEdges.map((c, i) => (
          <div key={c.id + i} className="ib-col-h" style={{ left: c.left, height: Math.max(headH, 12) }} onMouseDown={(e) => startColResize(e, el, i)} title="Drag to resize columns" />
        ))}
      {isSel && (
        <>
          <div className="ib-tbl-edge" style={{ left: -3 }} onMouseDown={(e) => startEdgeResize(e, band, el, "w")} />
          <div className="ib-tbl-edge" style={{ right: -3 }} onMouseDown={(e) => startEdgeResize(e, band, el, "e")} />
        </>
      )}
      {errors.length > 0 && isSel && <div className="ib-badge">{errors[0]}</div>}
    </div>
  );
}

export default function Canvas({ template, sample, zoom, selection, setSelection, state, showFormulas, snap, editingId, setEditingId }) {
  const { update, commitTransient, cancelTransient } = state;
  const ps = ZP.paperSize(template);
  const ctx = useMemo(() => ZP.createContext(template, sample), [template, sample]);
  const innerRef = useRef(null);
  const [innerH, setInnerH] = useState(300);
  const [guides, setGuides] = useState([]);
  const [dropBand, setDropBand] = useState(null);
  const [resizingBand, setResizingBand] = useState(null);
  const [contentH, setContentH] = useState(0);
  const d = template.defaults || {};

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setInnerH(el.offsetHeight);
      const root = el.querySelector(".zp-root");
      if (root) setContentH(root.offsetHeight / PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scaleMm = PX * zoom; // screen px per mm

  // Leaves any formula/property box so keyboard shortcuts reach the editor.
  const blurInputs = () => {
    const a = document.activeElement;
    if (a && a !== document.body && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) a.blur();
  };

  // Generic mouse drag helper.
  const drag = (e, onMove, onUp) => {
    e.preventDefault();
    e.stopPropagation();
    blurInputs();
    const sx = e.clientX;
    const sy = e.clientY;
    let moved = false;
    const mm = (ev) => {
      const dx = (ev.clientX - sx) / scaleMm;
      const dy = (ev.clientY - sy) / scaleMm;
      if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 3) return;
      moved = true;
      onMove(dx, dy, ev);
    };
    const up = (ev) => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("keydown", esc);
      setGuides([]);
      if (moved) onUp && onUp(ev, true);
      else onUp && onUp(ev, false);
    };
    const esc = (ev) => {
      if (ev.key === "Escape") {
        window.removeEventListener("mousemove", mm);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("keydown", esc);
        setGuides([]);
        cancelTransient();
      }
    };
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", up);
    window.addEventListener("keydown", esc);
  };

  const gridSnap = (v) => (snap ? Math.round(v * 2) / 2 : r2(v));

  const bandAtPoint = (x, y) => {
    const els = document.elementsFromPoint(x, y);
    for (const n of els) {
      const b = n.closest && n.closest("[data-band-id]");
      if (b) return b;
    }
    return null;
  };

  // Alt+click anywhere on the page picks the element underneath what is on top
  // (also through tables); repeated Alt+clicks cycle down the stack.
  const altSelect = (e) => {
    if (!e.altKey || e.button !== 0 || editingId) return;
    const stack = [];
    document.elementsFromPoint(e.clientX, e.clientY).forEach((n) => {
      const id = n.classList && n.classList.contains("ib-el") && n.getAttribute("data-el-id");
      if (id && !stack.includes(id)) stack.push(id);
    });
    if (!stack.length) return;
    e.preventDefault();
    e.stopPropagation();
    const hitTable = document.elementsFromPoint(e.clientX, e.clientY).some((n) => n.classList && n.classList.contains("ib-tbl"));
    const cur = stack.findIndex((id) => selection.ids.includes(id));
    // over a table the top-most element is already "underneath", so start there
    const nextId = stack[cur >= 0 ? (cur + 1) % stack.length : hitTable ? 0 : Math.min(1, stack.length - 1)];
    const owner = template.bands.find((b) => (b.elements || []).some((x) => x.id === nextId));
    if (owner) setSelection({ bandId: owner.id, ids: [nextId], colId: null });
  };

  const startMove = (e, band, el) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    let ids = selection.ids;
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      ids = ids.includes(el.id) ? ids.filter((x) => x !== el.id) : selection.bandId === band.id ? [...ids, el.id] : [el.id];
      setSelection({ bandId: band.id, ids, colId: null });
      return;
    }
    if (!ids.includes(el.id) || selection.bandId !== band.id) {
      ids = [el.id];
      setSelection({ bandId: band.id, ids, colId: null });
    }
    if (editingId) return;
    const starts = (band.elements || []).filter((x) => ids.includes(x.id)).map((x) => ({ id: x.id, x: x.x, y: x.y, w: x.w, h: x.h }));
    const primary = starts.find((s) => s.id === el.id);
    const others = (band.elements || []).filter((x) => !ids.includes(x.id));
    const thresh = 1.2 / zoom;
    drag(
      e,
      (dx, dy, ev) => {
        let nx = primary.x + dx;
        let ny = primary.y + dy;
        let g = [];
        if (!ev.altKey) {
          const s = snapPositions({ x: nx, y: ny, w: primary.w, h: primary.h }, others, ps.contentWidth, band.height || 10, thresh);
          if (s.guides.length) g = s.guides;
          nx += s.dx;
          ny += s.dy;
          if (!s.guides.some((q) => q.x !== undefined)) nx = gridSnap(nx);
          if (!s.guides.some((q) => q.y !== undefined)) ny = gridSnap(ny);
        }
        const ddx = nx - primary.x;
        const ddy = ny - primary.y;
        setGuides(g.map((q) => ({ ...q, bandId: band.id })));
        const over = bandAtPoint(ev.clientX, ev.clientY);
        setDropBand(over && over.getAttribute("data-band-id") !== band.id && over.getAttribute("data-band-type") === "fixed" ? over.getAttribute("data-band-id") : null);
        update(
          (t) => {
            const b = t.bands.find((x) => x.id === band.id);
            b.elements.forEach((x) => {
              const s = starts.find((q) => q.id === x.id);
              if (s) {
                x.x = r2(s.x + ddx);
                x.y = r2(s.y + ddy);
              }
            });
          },
          { transient: true }
        );
      },
      (ev, moved) => {
        setDropBand(null);
        if (!moved) return;
        const over = bandAtPoint(ev.clientX, ev.clientY);
        const targetId = over && over.getAttribute("data-band-type") === "fixed" ? over.getAttribute("data-band-id") : null;
        if (targetId && targetId !== band.id) {
          // move the elements into the section they were dropped on
          const srcRect = document.querySelector(`[data-band-id="${band.id}"]`).getBoundingClientRect();
          const dstRect = over.getBoundingClientRect();
          const offset = (srcRect.top - dstRect.top) / scaleMm;
          update(
            (t) => {
              const src = t.bands.find((x) => x.id === band.id);
              const dst = t.bands.find((x) => x.id === targetId);
              const moving = src.elements.filter((x) => ids.includes(x.id));
              src.elements = src.elements.filter((x) => !ids.includes(x.id));
              moving.forEach((x) => {
                x.y = r2(Math.max(0, x.y + offset));
                dst.elements.push(x);
              });
            },
            { transient: true }
          );
          setSelection({ bandId: targetId, ids, colId: null });
        }
        commitTransient();
      }
    );
  };

  const startResize = (e, band, el, h) => {
    const s = { x: el.x, y: el.y, w: el.w, h: el.h };
    drag(
      e,
      (dx, dy, ev) => {
        let { x, y, w, h: hh } = s;
        if (h.includes("e")) w = s.w + dx;
        if (h.includes("s")) hh = s.h + dy;
        if (h.includes("w")) {
          x = s.x + dx;
          w = s.w - dx;
        }
        if (h.includes("n")) {
          y = s.y + dy;
          hh = s.h - dy;
        }
        if (ev.shiftKey && el.type !== "line") {
          const ratio = s.w / s.h || 1;
          if (h.length === 2) hh = w / ratio;
        }
        if (!ev.altKey) {
          if (h.includes("e")) w = gridSnap(x + w) - x;
          if (h.includes("s")) hh = gridSnap(y + hh) - y;
          if (h.includes("w")) {
            const nx = gridSnap(x);
            w += x - nx;
            x = nx;
          }
          if (h.includes("n")) {
            const ny = gridSnap(y);
            hh += y - ny;
            y = ny;
          }
        }
        const minH = el.type === "line" ? 0.5 : 1;
        w = Math.max(1, w);
        hh = Math.max(minH, hh);
        update(
          (t) => {
            const b = t.bands.find((q) => q.id === band.id);
            const target = b.elements.find((q) => q.id === el.id);
            Object.assign(target, { x: r2(x), y: r2(y), w: r2(w), h: r2(hh) });
          },
          { transient: true }
        );
      },
      (ev, moved) => moved && commitTransient()
    );
  };

  const startBandResize = (e, band) => {
    const start = band.height || 10;
    setResizingBand(band.id);
    drag(
      e,
      (dx, dy) =>
        update(
          (t) => {
            const b = t.bands.find((q) => q.id === band.id);
            const minH = Math.max(2, ...(b.elements || []).map((x) => (x.type === "table" ? 0 : x.y + Math.min(x.h, 2))));
            b.height = Math.max(minH, gridSnap(start + dy));
          },
          { transient: true }
        ),
      (ev, moved) => {
        setResizingBand(null);
        if (moved) commitTransient();
      }
    );
  };

  const startColResize = (e, table, edgeIndex) => {
    // edgeIndex is between visible columns edgeIndex and edgeIndex+1
    const vis = ctx.visibleColumns(table, []).map((c) => c.col.id);
    const leftId = vis[edgeIndex];
    const rightId = vis[edgeIndex + 1];
    const cols = table.columns || [];
    const total = cols.filter((c) => vis.includes(c.id)).reduce((a, c) => a + Math.max(0.1, +c.width || 10), 0);
    const perMm = total / (table.w || 1);
    const L = cols.find((c) => c.id === leftId);
    const R = cols.find((c) => c.id === rightId);
    const sl = +L.width || 10;
    const sr = +R.width || 10;
    drag(
      e,
      (dx) => {
        let delta = dx * perMm;
        delta = Math.max(-(sl - 0.5), Math.min(sr - 0.5, delta));
        update(
          (t) => {
            for (const b of t.bands)
              for (const x of b.elements || [])
                if (x.id === table.id) {
                  x.columns.find((c) => c.id === leftId).width = r2(sl + delta);
                  x.columns.find((c) => c.id === rightId).width = r2(sr - delta);
                }
          },
          { transient: true }
        );
      },
      (ev, moved) => moved && commitTransient()
    );
  };

  const startEdgeResize = (e, band, table, side) => {
    const sx = table.x || 0;
    const sw = table.w || 10;
    drag(
      e,
      (dx) =>
        update(
          (t) => {
            const b = t.bands.find((q) => q.id === band.id);
            const x = b.elements.find((q) => q.id === table.id);
            if (side === "e") x.w = Math.max(10, Math.min(ps.contentWidth - sx, gridSnap(sw + dx)));
            else {
              const nx = Math.max(0, Math.min(sx + sw - 10, gridSnap(sx + dx)));
              x.x = nx;
              x.w = r2(sw - (nx - sx));
            }
          },
          { transient: true }
        ),
      (ev, moved) => moved && commitTransient()
    );
  };

  // Drop from the Insert / Fields panels.
  const onDrop = (e, band) => {
    e.preventDefault();
    setDropBand(null);
    const raw = e.dataTransfer.getData("application/x-ib");
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = gridSnap((e.clientX - rect.left) / scaleMm);
    const y = gridSnap((e.clientY - rect.top) / scaleMm);
    const el = newElement(payload.type, { x, y }, payload.extra || {});
    el.x = Math.max(0, Math.min(ps.contentWidth - el.w, el.x));
    el.y = Math.max(0, el.y);
    update((t) => {
      const b = t.bands.find((q) => q.id === band.id);
      b.elements.push(el);
      if (el.y + el.h > b.height) b.height = r2(el.y + el.h + 1);
    });
    setSelection({ bandId: band.id, ids: [el.id], colId: null });
  };

  const finishEdit = (band, el, value) => {
    setEditingId(null);
    if (value === null || value === el.content) return;
    update((t) => {
      const b = t.bands.find((q) => q.id === band.id);
      b.elements.find((q) => q.id === el.id).content = value;
    });
  };

  // where printed pages would break (approximate: ignores repeated headers)
  const pageBreaks = [];
  if (!ps.continuous && ps.contentHeight > 0) for (let y = ps.contentHeight; y < contentH - 1; y += ps.contentHeight) pageBreaks.push(y);

  const fontFamily = ZP.fonts[d.fontFamily] || d.fontFamily || "Arial";

  return (
    <div
      className="flex-1 overflow-auto bg-gray-200"
      onMouseDownCapture={(e) => {
        if (editingId) return;
        blurInputs();
        altSelect(e);
      }}
      onMouseDown={() => !editingId && setSelection({ bandId: null, ids: [], colId: null })}
    >
      <style>{ZP.baseCss() + EDITOR_CSS}</style>
      <div style={{ width: (ps.width + GUTTER + 10) * scaleMm, height: innerH * zoom + 40, position: "relative", margin: "0 auto" }}>
        <div ref={innerRef} style={{ position: "absolute", left: GUTTER * scaleMm, top: 20, transform: `scale(${zoom})`, transformOrigin: "top left", width: ps.width + "mm" }}>
          <div
            style={{
              width: ps.width + "mm",
              minHeight: ps.continuous ? "40mm" : ps.height + "mm",
              padding: `${ps.margins.top}mm ${ps.margins.right}mm ${ps.margins.bottom}mm ${ps.margins.left}mm`,
              background: "#fff",
              boxShadow: "0 1px 6px rgba(0,0,0,.25)",
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            <div className="zp-root" style={{ width: ps.contentWidth + "mm", fontFamily, fontSize: (d.fontSize || 9) + "pt", color: d.color || "#000", position: "relative" }}>
              {(template.bands || []).map((band) => {
                const bandSel = selection.bandId === band.id && !selection.ids.length;
                const hiddenBand = !ctx.isVisible(band, ctx.rootScope(), []);
                const label = (
                  <div
                    className={`ib-band-label ${selection.bandId === band.id ? "ib-sel" : ""}`}
                    style={{ right: `calc(100% + ${ps.margins.left + 1}mm)`, maxWidth: GUTTER - 2 + "mm", opacity: hiddenBand ? 0.6 : 1 }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setSelection({ bandId: band.id, ids: [], colId: null });
                    }}
                    title={band.name}
                  >
                    {band.type === "table" ? "▦ " : ""}
                    {band.name || "Section"}
                    {band.repeat === "top" || band.repeat === "bottom" ? " ⟳" : ""}
                    {hiddenBand ? " (hidden)" : ""}
                  </div>
                );
                if (band.type === "table") {
                  return (
                    <div
                      key={band.id}
                      data-band-id={band.id}
                      data-band-type="table"
                      className={`zp-band ib-band ${bandSel ? "ib-sel" : ""}`}
                      style={{ position: "relative", paddingTop: (band.paddingTop || 0) + "mm", paddingBottom: (band.paddingBottom || 0) + "mm", opacity: hiddenBand ? 0.5 : 1, minHeight: "4mm" }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setSelection({ bandId: band.id, ids: [], colId: null });
                      }}
                    >
                      {label}
                      {(band.elements || []).map((el) => (
                        <TableView key={el.id} el={el} band={band} ctx={ctx} selection={selection} setSelection={setSelection} startColResize={startColResize} startEdgeResize={startEdgeResize} />
                      ))}
                    </div>
                  );
                }
                return (
                  <div
                    key={band.id}
                    data-band-id={band.id}
                    data-band-type="fixed"
                    className={`zp-band ib-band ${bandSel ? "ib-sel" : ""} ${dropBand === band.id ? "ib-drop" : ""}`}
                    style={{ position: "relative", height: (band.height || 10) + "mm", background: band.bg || undefined, opacity: hiddenBand ? 0.5 : 1 }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      if (editingId) return;
                      setSelection({ bandId: band.id, ids: [], colId: null });
                    }}
                    onDragOver={(e) => {
                      if (e.dataTransfer.types.includes("application/x-ib")) {
                        e.preventDefault();
                        setDropBand(band.id);
                      }
                    }}
                    onDragLeave={() => setDropBand(null)}
                    onDrop={(e) => onDrop(e, band)}
                  >
                    {label}
                    {(band.elements || []).map((el) => (
                      <ElementView
                        key={el.id}
                        el={el}
                        ctx={ctx}
                        selected={selection.ids.includes(el.id)}
                        showFormulas={showFormulas}
                        editing={editingId === el.id}
                        onEditDone={(v) => finishEdit(band, el, v)}
                        onMouseDown={(e) => startMove(e, band, el)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (el.type === "text") setEditingId(el.id);
                        }}
                      />
                    ))}
                    {/* Selection outline + handles sit above every element, so the
                        element itself keeps its real front/back position. */}
                    {(band.elements || [])
                      .filter((el) => selection.ids.includes(el.id) && editingId !== el.id)
                      .map((el) => (
                        <div
                          key={"sel-" + el.id}
                          className="ib-sel-frame"
                          onMouseDown={(e) => startMove(e, band, el)}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (el.type === "text") setEditingId(el.id);
                          }}
                          style={{ left: el.x + "mm", top: el.y + "mm", width: el.w + "mm", height: el.h + "mm", transform: el.rotate ? `rotate(${el.rotate}deg)` : undefined }}
                        >
                          {selection.ids.length === 1 && HANDLES.map((h) => <div key={h} className="ib-h" style={handleStyle(h)} onMouseDown={(e) => startResize(e, band, el, h)} />)}
                        </div>
                      ))}
                    {guides
                      .filter((g) => g.bandId === band.id)
                      .map((g, i) =>
                        g.x !== undefined ? (
                          <div key={i} className="ib-guide" style={{ left: g.x + "mm", top: -2000, bottom: -2000, width: 1 / zoom }} />
                        ) : (
                          <div key={i} className="ib-guide" style={{ top: g.y + "mm", left: "-20mm", right: "-20mm", height: 1 / zoom }} />
                        )
                      )}
                    <div className={`ib-band-resize ${resizingBand === band.id ? "ib-on" : ""}`} onMouseDown={(e) => startBandResize(e, band)} title="Drag to change section height" />
                  </div>
                );
              })}
              {pageBreaks.map((y, i) => (
                <div key={i} className="ib-pagebreak" style={{ top: y + "mm" }}>
                  <span>page {i + 2} ≈</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

